import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Phone, CheckCircle, ArrowRight, ArrowLeft, User, MapPin, FileText, CreditCard, Loader2, AlertCircle, MessageCircle } from 'lucide-react';
import { companyInfo } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookTestPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [bookingId, setBookingId] = useState(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    niNumber: '',
    phone: '',
    email: '',
    postcode: '',
    address: '',
    city: '',
    testType: ''
  });

  const testTypes = [
    {
      id: 'operatives',
      name: 'CITB Operatives Test',
      description: 'For construction workers and labourers',
      icon: '👷'
    },
    {
      id: 'specialist',
      name: 'CITB Specialist Test',
      description: 'For specialist trades and skilled workers',
      icon: '🔧'
    },
    {
      id: 'manager',
      name: 'CITB Manager/Professional Test',
      description: 'For supervisors, managers and professionals',
      icon: '📋'
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const formatNINumber = (value) => {
    // Remove all non-alphanumeric characters
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    // Format as XX XX XX XX X
    let formatted = '';
    for (let i = 0; i < cleaned.length && i < 9; i++) {
      if (i === 2 || i === 4 || i === 6 || i === 8) {
        formatted += ' ';
      }
      formatted += cleaned[i];
    }
    return formatted;
  };

  const handleNIChange = (e) => {
    const formatted = formatNINumber(e.target.value);
    setFormData(prev => ({ ...prev, niNumber: formatted }));
    if (errors.niNumber) {
      setErrors(prev => ({ ...prev, niNumber: '' }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    
    if (!formData.niNumber.trim()) {
      newErrors.niNumber = 'National Insurance number is required';
    } else if (formData.niNumber.replace(/\s/g, '').length !== 9) {
      newErrors.niNumber = 'Invalid NI number format';
    }
    
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    if (!formData.testType) {
      setErrors({ testType: 'Please select a test type' });
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validateStep2()) {
      createBookingAndProceed();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const createBookingAndProceed = async () => {
    setIsLoading(true);
    try {
      // Create booking
      const response = await fetch(`${API}/bookings/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create booking');
      }

      const booking = await response.json();
      setBookingId(booking.id);
      setCurrentStep(3);
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const proceedToPayment = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/bookings/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: bookingId,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { checkout_url } = await response.json();
      window.location.href = checkout_url;
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const selectedTestType = testTypes.find(t => t.id === formData.testType);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-construction-darkblue text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Book Your CITB Test</h1>
          <p className="text-gray-300">Complete the form below to book your health and safety test</p>
        </div>
      </section>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-center gap-4 md:gap-8">
            {[
              { num: 1, label: 'Personal Details', icon: User },
              { num: 2, label: 'Test Type', icon: FileText },
              { num: 3, label: 'Payment', icon: CreditCard }
            ].map((step, index) => (
              <React.Fragment key={step.num}>
                <div className={`flex items-center gap-2 ${currentStep >= step.num ? 'text-construction-darkblue' : 'text-gray-400'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                    currentStep > step.num ? 'bg-green-500 text-white' :
                    currentStep === step.num ? 'bg-construction-yellow text-construction-darkblue' :
                    'bg-gray-200 text-gray-500'
                  }`}>
                    {currentStep > step.num ? <CheckCircle size={20} /> : step.num}
                  </div>
                  <span className="hidden md:block font-medium">{step.label}</span>
                </div>
                {index < 2 && (
                  <div className={`w-12 md:w-24 h-1 rounded ${currentStep > step.num ? 'bg-green-500' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          
          {/* Step 1: Personal Details */}
          {currentStep === 1 && (
            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-6 flex items-center gap-2">
                <User className="text-construction-yellow" />
                Personal Details
              </h2>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow ${errors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="John"
                      data-testid="input-first-name"
                    />
                    {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow ${errors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Smith"
                      data-testid="input-last-name"
                    />
                    {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">National Insurance Number *</label>
                  <input
                    type="text"
                    name="niNumber"
                    value={formData.niNumber}
                    onChange={handleNIChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow ${errors.niNumber ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="QQ 12 34 56 C"
                    maxLength={13}
                    data-testid="input-ni"
                  />
                  {errors.niNumber && <p className="text-red-500 text-sm mt-1">{errors.niNumber}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="07123 456789"
                      data-testid="input-phone"
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="john@example.com"
                      data-testid="input-email"
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Postcode *</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleChange}
                        className={`flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow ${errors.postcode ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="SW1A 1AA"
                        data-testid="input-postcode"
                      />
                    </div>
                    {errors.postcode && <p className="text-red-500 text-sm mt-1">{errors.postcode}</p>}
                    <a 
                      href="https://www.royalmail.com/find-a-postcode" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-construction-yellow hover:underline mt-1 inline-block"
                    >
                      Find your postcode
                    </a>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">City *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow ${errors.city ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="London"
                      data-testid="input-city"
                    />
                    {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Full Address *</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows={3}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-construction-yellow resize-none ${errors.address ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="123 High Street, Apartment 4B"
                    data-testid="input-address"
                  />
                  {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                </div>
              </div>

              <div className="mt-8 flex justify-end">
                <button
                  onClick={handleNext}
                  className="inline-flex items-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-3 rounded-lg font-bold hover:bg-yellow-500 transition-all"
                  data-testid="btn-next-step1"
                >
                  Next Step
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Test Type Selection */}
          {currentStep === 2 && (
            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-6 flex items-center gap-2">
                <FileText className="text-construction-yellow" />
                Select Your Test Type
              </h2>

              <div className="space-y-4">
                {testTypes.map((test) => (
                  <label
                    key={test.id}
                    className={`block p-6 border-2 rounded-xl cursor-pointer transition-all ${
                      formData.testType === test.id
                        ? 'border-construction-yellow bg-construction-yellow/10'
                        : 'border-gray-200 hover:border-construction-yellow/50'
                    }`}
                    data-testid={`test-type-${test.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <input
                        type="radio"
                        name="testType"
                        value={test.id}
                        checked={formData.testType === test.id}
                        onChange={handleChange}
                        className="w-5 h-5 text-construction-yellow"
                      />
                      <div className="text-3xl">{test.icon}</div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-construction-darkblue">{test.name}</h3>
                        <p className="text-gray-600">{test.description}</p>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {errors.testType && (
                <p className="text-red-500 text-sm mt-4 flex items-center gap-1">
                  <AlertCircle size={16} />
                  {errors.testType}
                </p>
              )}

              {errors.submit && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {errors.submit}
                </div>
              )}

              <div className="mt-8 flex justify-between">
                <button
                  onClick={handleBack}
                  className="inline-flex items-center gap-2 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-300 transition-all"
                >
                  <ArrowLeft size={20} />
                  Back
                </button>
                <button
                  onClick={handleNext}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-3 rounded-lg font-bold hover:bg-yellow-500 transition-all disabled:opacity-50"
                  data-testid="btn-next-step2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Processing...
                    </>
                  ) : (
                    <>
                      Continue to Payment
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Payment */}
          {currentStep === 3 && (
            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
              <h2 className="text-2xl font-bold text-construction-darkblue mb-6 flex items-center gap-2">
                <CreditCard className="text-construction-yellow" />
                Payment
              </h2>

              {/* Order Summary */}
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <h3 className="font-bold text-lg text-construction-darkblue mb-4">Order Summary</h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-medium">{formData.firstName} {formData.lastName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium">{formData.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Test Type:</span>
                    <span className="font-medium">{selectedTestType?.name}</span>
                  </div>
                  <hr className="my-4" />
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total:</span>
                    <span className="text-construction-darkblue">£53.00</span>
                  </div>
                </div>
              </div>

              {/* What's Included */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
                <h3 className="font-bold text-green-800 mb-3">What's Included:</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-green-700">
                    <CheckCircle size={18} />
                    CITB Health & Safety Test
                  </li>
                  <li className="flex items-center gap-2 text-green-700">
                    <CheckCircle size={18} />
                    Test Booking & Registration
                  </li>
                  <li className="flex items-center gap-2 text-green-700">
                    <CheckCircle size={18} />
                    Confirmation Email
                  </li>
                  <li className="flex items-center gap-2 text-green-700">
                    <CheckCircle size={18} />
                    Support Team Assistance
                  </li>
                </ul>
              </div>

              {errors.submit && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {errors.submit}
                </div>
              )}

              <div className="flex flex-col gap-4">
                <button
                  onClick={proceedToPayment}
                  disabled={isLoading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all disabled:opacity-50"
                  data-testid="btn-pay"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin" size={24} />
                      Redirecting to Payment...
                    </>
                  ) : (
                    <>
                      <CreditCard size={24} />
                      Pay £53.00 Now
                    </>
                  )}
                </button>

                <button
                  onClick={handleBack}
                  disabled={isLoading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-300 transition-all"
                >
                  <ArrowLeft size={20} />
                  Back
                </button>
              </div>

              <p className="text-center text-sm text-gray-500 mt-4">
                Secure payment powered by Stripe
              </p>
            </div>
          )}

          {/* Help Section */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">Need help with your booking?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 bg-construction-darkblue text-white px-6 py-3 rounded-lg font-bold hover:bg-black transition-all"
              >
                <Phone size={20} />
                {companyInfo.phone}
              </a>
              <a
                href={`https://wa.me/${companyInfo.whatsapp.replace(/\+/g, '')}?text=Hi, I need help booking my CITB test`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700 transition-all"
              >
                <MessageCircle size={20} />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookTestPage;
